import { useState, useMemo, useEffect, type ChangeEvent } from 'react';
import { IconButton, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toMillis } from '@/utils/folder.utils';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { FlexCenVer, FlexCenVerSpaceBetween, FlexCol, FlexColCenHorVer } from '@/UI/styles';
import { Input } from '@/UI/Input/Input';
import { SearchIcon } from '@/UI/Icons/SearchIcon';
import { SmileyXEyes } from '@/UI/Icons/SmileyXEyes';
import { ArchiveIcon } from '@/UI/Icons/ArchiveIcon';
import { useGetArchiveItems, useRestoreWorkflows, useRestoreFolders } from '@/queries/useArchive';
import { useGlobalStore } from '@/state/global.state';
import { TextSmRg, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { calculateArchiveFolderData, countFilesInFolder } from '../utils/dashboard.utils';
import { ErrorDisplay } from '../../Common/Error/ErrorDisplay';
import { EmptyState } from '../../Common/EmptyState/EmptyState';
import ArchiveCardView from './ArchiveCardView';
import ArchiveListView from './ArchiveListView';
import type { ArchiveItem } from './archive.types';

const DashboardArchive = () => {
  const { t } = useTranslation();
  const { updateSnackbarData } = useGlobalStore();
  const { data, isLoading, isError, refetch } = useGetArchiveItems();

  const [isListView, setIsListView] = useState<boolean>(() => {
    const saved = localStorage.getItem('isListView');
    return saved !== null ? saved === 'true' : false;
  });

  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    localStorage.setItem('isListView', isListView.toString());
  }, [isListView]);

  const allWorkflows: ArchiveItem[] = useMemo(() => data?.workflows ?? [], [data?.workflows]);
  const allFolders: ArchiveItem[] = useMemo(() => data?.folders ?? [], [data?.folders]);

  const archivedFolderIds = useMemo(() => new Set(allFolders.map((f) => f.id)), [allFolders]);

  const folderFileCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const folder of allFolders) {
      counts.set(folder.id, countFilesInFolder(folder.id, allFolders, allWorkflows));
    }
    return counts;
  }, [allFolders, allWorkflows]);

  const archiveFolderData = useMemo(
    () => calculateArchiveFolderData(allFolders, allWorkflows),
    [allFolders, allWorkflows],
  );

  // Only show top-level folders (whose parent is not in the archive)
  const filteredFolders = useMemo(() => {
    const topLevel = allFolders.filter((f) => !f.parentId || !archivedFolderIds.has(f.parentId));
    if (searchTerm) {
      return topLevel.filter((folder) => folder.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    return topLevel;
  }, [allFolders, archivedFolderIds, searchTerm]);

  // Only show workflows whose folder is not in the archive
  const filteredWorkflows = useMemo(() => {
    const visible = allWorkflows.filter((r) => !r.parentId || !archivedFolderIds.has(r.parentId));
    if (searchTerm) {
      return visible.filter((r) => r.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    return visible;
  }, [allWorkflows, archivedFolderIds, searchTerm]);

  const sortedItems = useMemo(() => {
    const items: ArchiveItem[] = [
      ...filteredFolders.map((f) => ({ ...f, itemType: 'folder' as const })),
      ...filteredWorkflows.map((r) => ({ ...r, itemType: 'workflow' as const })),
    ];
    return items.sort((a, b) => (toMillis(b.deletedAt) || 0) - (toMillis(a.deletedAt) || 0));
  }, [filteredFolders, filteredWorkflows]);

  const { mutate: restoreWorkflows } = useRestoreWorkflows();
  const { mutate: restoreFolders } = useRestoreFolders();
  const [restoringIds, setRestoringIds] = useState<Set<string>>(new Set());

  const onRestoreError = () => {
    updateSnackbarData({
      text: t(I18N_KEYS.DASHBOARD.PAGES.ARCHIVE.RESTORE_ERROR),
      isOpen: true,
      icon: null,
      severity: 'error',
    });
  };

  const handleRestore = (id: string, type: 'workflow' | 'folder') => {
    setRestoringIds((prev) => new Set(prev).add(id));
    const options = {
      onError: onRestoreError,
      onSettled: () =>
        setRestoringIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        }),
    };
    if (type === 'workflow') {
      restoreWorkflows([id], options);
    } else {
      restoreFolders([id], options);
    }
  };

  const handleSearchChange = (evt: ChangeEvent<HTMLInputElement>) => setSearchTerm(evt.target.value);

  const hasItems = allWorkflows.length > 0 || allFolders.length > 0;

  const renderSubheader = () => {
    if (!hasItems) {
      return null;
    }

    return (
      <FlexCenVerSpaceBetween
        data-testid="archive-top-menu-container"
        sx={{
          width: '100%',
          minHeight: 40,
          flexShrink: 0,
          my: 4,
        }}
      >
        <FlexCol>
          <TextStdRg text={t(I18N_KEYS.DASHBOARD.PAGES.ARCHIVE.TITLE)} color={color.White100} />
          <TextSmRg text={t(I18N_KEYS.DASHBOARD.PAGES.ARCHIVE.SUBTITLE)} color={color.White64_T} />
        </FlexCol>
        <FlexCenVer data-testid="archive-list-top-menu" sx={{ justifyContent: 'flex-end' }}>
          <Input
            placeholder={t(I18N_KEYS.GENERAL.SEARCH)}
            value={searchTerm}
            onChange={handleSearchChange}
            startAdornment={<SearchIcon />}
            sx={{ mr: 2 }}
          />
          <IconButton
            size="small"
            onClick={() => setIsListView(true)}
            sx={{ backgroundColor: isListView ? color.Super_Light_Transparent : '', borderRadius: 1 }}
          >
            <img src="/icons/list.svg" alt="list" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setIsListView(false)}
            sx={{ backgroundColor: !isListView ? color.Super_Light_Transparent : '', borderRadius: 1 }}
          >
            <img src="/icons/squares.svg" alt="squares" />
          </IconButton>
        </FlexCenVer>
      </FlexCenVerSpaceBetween>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return null;
    }

    if (isError) {
      return (
        <FlexColCenHorVer sx={{ width: '100%', height: '100%' }}>
          <ErrorDisplay
            icon={<SmileyXEyes />}
            title={t(I18N_KEYS.MAIN_DASHBOARD.ERROR_LOADING_RECIPES.TITLE)}
            description={null}
            actions={[
              { label: t(I18N_KEYS.MAIN_DASHBOARD.ERROR_LOADING_RECIPES.ACTION), onClick: () => void refetch() },
            ]}
          />
        </FlexColCenHorVer>
      );
    }

    if (!hasItems) {
      return (
        <EmptyState
          icon={<ArchiveIcon />}
          title={t(I18N_KEYS.DASHBOARD.PAGES.ARCHIVE.EMPTY_STATE.TITLE)}
          description={t(I18N_KEYS.DASHBOARD.PAGES.ARCHIVE.EMPTY_STATE.DESCRIPTION)}
          actions={[]}
          sx={{ paddingTop: 'max(15%, 144px)', alignItems: 'flex-start' }}
        />
      );
    }

    if (sortedItems.length === 0) {
      if (searchTerm) {
        return (
          <EmptyState
            icon={<ArchiveIcon />}
            title={t(I18N_KEYS.MAIN_DASHBOARD.SEARCH_NO_RESULTS.TITLE, { searchTerm })}
            description={t(I18N_KEYS.MAIN_DASHBOARD.SEARCH_NO_RESULTS.DESCRIPTION)}
            actions={[]}
            sx={{ paddingTop: '104px', alignItems: 'flex-start' }}
          />
        );
      }
      return (
        <EmptyState
          icon={<ArchiveIcon />}
          title={t(I18N_KEYS.DASHBOARD.PAGES.ARCHIVE.EMPTY_STATE.TITLE)}
          description={t(I18N_KEYS.DASHBOARD.PAGES.ARCHIVE.EMPTY_STATE.DESCRIPTION)}
          actions={[]}
          sx={{ paddingTop: 'max(15%, 144px)', alignItems: 'flex-start' }}
        />
      );
    }

    if (isListView) {
      return (
        <ArchiveListView
          items={sortedItems}
          onRestore={handleRestore}
          restoringIds={restoringIds}
          folderFileCounts={folderFileCount}
          archiveFolderData={archiveFolderData}
        />
      );
    }

    return (
      <ArchiveCardView
        items={sortedItems}
        onRestore={handleRestore}
        restoringIds={restoringIds}
        folderFileCounts={folderFileCount}
        archiveFolderData={archiveFolderData}
      />
    );
  };

  return (
    <Box
      component="main"
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        pb: 0,
      }}
    >
      <FlexCol sx={{ flexGrow: 1 }}>
        {renderSubheader()}
        {renderContent()}
      </FlexCol>
    </Box>
  );
};

export default DashboardArchive;
